import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { Constants } from '../helpers';
import { ItemsResponse, ReferenceCode } from '../interfaces';
import { ReferenceCodeModel } from '../models';
import { BaseService } from './base.service';

@Injectable()
export class RefCodeService extends BaseService<ReferenceCodeModel> {
	//this will keep the observable for caching
	public referenceCodesObservable: Observable<any> = null;
	//lists of referenceCodes that we have received
	public referenceCodes: ReferenceCode[];

	constructor(httpClient: HttpClient) {
		super(httpClient, Constants.END_POINTS.referenceCode, ReferenceCodeModel);
		this.referenceCodes = [];
	}

	//Get the reference codes from database
	//when we do not have in the cache.
	public getRefCodesWithTypesFromDatabase = (codeTypes: string[], isActive: boolean) => {
		this.referenceCodesObservable = this.httpClient
			.post<ItemsResponse>(`${Constants.BASE_API_URL}/referencecode/list`, {
				codeType: codeTypes,
				isActive,
			})
			.pipe(
				map(req => {
					const result = req.items.reduce((prev, code) => {
						if (prev[code.codeType] === undefined) {
							prev[code.codeType] = [];
						}

						prev[code.codeType].push(code);

						return prev;
					}, {});

					Object.keys(result).forEach((key) => {
						result[key].sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1));
					})

					return result;
				}),
				tap(referenceCodes => this.referenceCodes = { ...this.referenceCodes, ...referenceCodes }),
				shareReplay(1)
			);

		return this.referenceCodesObservable;
	};

	/**
	 * Gets all referenceCodes with the given comma seperated types
	 * @param {string[]} codeTypes
	 * @param {boolean} isActive
	 * @returns {Observable<any>}
	 */
	public getRefCodesWithTypesAndStatus(codeTypes: string[] = [], isActive: boolean) {
		//first call
		if (!this.referenceCodesObservable) {
			return this.getRefCodesWithTypesFromDatabase(codeTypes, isActive);
		}

		//determine which types are missing from the cache
		const missingTypes = codeTypes.filter(t => !this.referenceCodes[t]);
		if (missingTypes.length > 0) {
			//fetch only the missing types
			return this.getRefCodesWithTypesFromDatabase(missingTypes, isActive).pipe(map(() => this.referenceCodes));
		}

		//everything is already cached
		return of(this.referenceCodes);
	}

	/**
	 * Gets all ReferenceCode types from json file in server side
	 * @returns {Observable<any>}
	 */
	public getRefCodeTypes() {
		return this.httpClient.post<ItemsResponse>(`${Constants.BASE_API_URL}/referencecode/types`, {}).pipe(map(res => res.items));
	}
}

@Injectable()
export class RefCodeTypesResolver {
	constructor(private refCodeService: RefCodeService) {}

	public resolve(): Observable<any> | Promise<any> | any {
		return this.refCodeService.getRefCodeTypes();
	}
}

@Injectable()
export class RefCodeWithTypesResolver {
	constructor(private refCodeService: RefCodeService) {}

	public resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
		return this.refCodeService.getRefCodesWithTypesFromDatabase(route.data.resolverCodeTypes, true);
	}
}

@Injectable()
export class ActiveRefCodeWithTypesResolver {
	constructor(private refCodeService: RefCodeService) {}

	public resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
		return this.refCodeService.getRefCodesWithTypesAndStatus(route.data.resolverCodeTypes, true);
	}
}
